import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { applicationConfiguration } from 'app/modules/shared'
import { setCoralogixUser, initCoralogix } from './coralogix'
import { AuthContext } from 'app/modules/auth/context'
import { initOtel, shutdownOtel } from './otel'
import { VersionInfo } from 'app/modules/admin/features/settings/appearance/types'
import { settingsService } from 'app/modules/admin/services/settings.service'
import { logger } from 'app/components/logger'
import { getOneTrustConsent } from 'app/modules/auth/helper/get-cookie'

// OneTrust Analytics/Performance category ID
const ONETRUST_ANALYTICS_CATEGORY = 'C0002'

const monitoringContext = createContext({})

// Handle client-side monitoring
export const WithMonitoringContext = ({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element | null => {
  const config = useContext(applicationConfiguration)
  const auth = useContext(AuthContext)
  const [version, setVersion] = useState<VersionInfo['webservicefront_version']>('')
  const [analyticsConsent, setAnalyticsConsent] = useState(() => getOneTrustConsent(ONETRUST_ANALYTICS_CATEGORY))
  const monitoringIgnoreUrls: (string | RegExp)[] = (config?.tracing?.monitoringIgnoreUrls || []).map((pattern) => {
    const match = pattern.match(/^\/(.+)\/$/)
    return match ? new RegExp(match[1]) : pattern
  })

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await settingsService.getVersionInfo()
        setVersion(response.webservicefront_version)
      } catch (err) {
        logger.warn('Failed to fetch version info')
      }
    }

    fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for OneTrust consent changes
  useEffect(() => {
    const handleConsentChange = () => {
      setAnalyticsConsent(getOneTrustConsent(ONETRUST_ANALYTICS_CATEGORY))
    }

    document.addEventListener('OptanonConsentChanged', handleConsentChange)
    return () => document.removeEventListener('OptanonConsentChanged', handleConsentChange)
  }, [])

  // This context does not provide any data
  const provided = useMemo(() => ({}), [])

  // Setup or shutdown coralogix based on consent
  useEffect(() => {
    initCoralogix(monitoringIgnoreUrls, analyticsConsent ? config?.tracing : undefined, version)
    setCoralogixUser(auth.user)
  }, [config?.tracing, version, analyticsConsent, auth.user])

  // Setup or shutdown OpenTelemetry based on consent
  useEffect(() => {
    if (analyticsConsent) {
      initOtel(monitoringIgnoreUrls, config?.tracing, version)
    } else {
      shutdownOtel()
    }
    return () => shutdownOtel()
  }, [config?.tracing, version, analyticsConsent])

  // Set Coralogix user context when user changes
  useEffect(() => {
    setCoralogixUser(auth.user)
  }, [auth.user])

  return <monitoringContext.Provider value={provided}>{children}</monitoringContext.Provider>
}
